import { apiClient } from "@/services/apiClient";

export type PlanCheckoutReturnContext = "onboarding" | "settings" | "shared_link";

export interface CreatePlanCheckoutParams {
  planId: string;
  billingInterval: "monthly" | "annual";
  returnContext: PlanCheckoutReturnContext;
}

export interface CreatePlanCheckoutResponse {
  checkoutId: string;
  redirectUrl: string | null;
  status: "pending" | "paid";
  amountMinor: number;
  currency: string;
  /** fiscalo.link/p/:token URL — pass to the buyer, NOT the long dLocal redirectUrl. */
  shortUrl: string | null;
}

export function createPlanCheckout(
  params: CreatePlanCheckoutParams
): Promise<CreatePlanCheckoutResponse> {
  return apiClient
    .post<CreatePlanCheckoutResponse>("/tenants/current/plan-checkout", params)
    .then((res) => res.data);
}

/**
 * Plan checkout for a specific tenant the user is a member of —
 * lets accountants generate a payment link for any of their tenants
 * without first switching contexts. Backend enforces membership +
 * accountant/owner/admin role.
 */
export function createPlanCheckoutForTenant(
  tenantId: string,
  params: CreatePlanCheckoutParams
): Promise<CreatePlanCheckoutResponse> {
  return apiClient
    .post<CreatePlanCheckoutResponse>(
      `/tenants/${tenantId}/plan-checkout`,
      params
    )
    .then((res) => res.data);
}

export interface BankTransferCheckoutResponse {
  checkoutId: string;
  status: "paid";
  /**
   * False when a MONTHLY transfer is held pending review — paid, but the plan
   * is NOT granted. Absent on older responses, so treat undefined as true.
   * Annual always activates. Spec a9b1ccad.
   */
  activated?: boolean;
  amountMinor: number;
  currency: string;
  receiptUrl: string;
  /**
   * AI verdict on the receipt: 'verified' | 'needs_review' | 'mismatch' |
   * 'unverified' (null if verification didn't run). For ANNUAL the plan
   * activates regardless and this is an internal review signal; for MONTHLY a
   * 'mismatch'/'needs_review' verdict holds the grant — see `activated`.
   */
  verificationStatus: string | null;
}

/**
 * Pay the ACTIVE tenant's plan by bank transfer: upload the wire receipt
 * against the session's own tenant. This is the path a business owner drives
 * for themselves from Settings → Facturación; the accountant-driven variant
 * below targets a tenant by id instead.
 *
 * Both intervals since spec a9b1ccad. Monthly verifies the receipt BEFORE
 * granting, so check `activated` on the response — false means paid but held
 * pending review, and telling the tenant their plan is active would be a lie
 * they discover when they try to emit.
 */
export function createBankTransferCheckout(params: {
  planId: string;
  billingInterval: "annual" | "monthly";
  receipt: File;
  /**
   * Settle this pending checkout rather than creating a new one. Required
   * whenever the payer was shown a payment reference, which is derived from
   * the checkout id — a new row would make that reference unverifiable.
   */
  settleCheckoutId?: string;
}): Promise<BankTransferCheckoutResponse> {
  const formData = new FormData();
  formData.append("receipt", params.receipt);
  formData.append("planId", params.planId);
  formData.append("billingInterval", params.billingInterval);
  if (params.settleCheckoutId) {
    formData.append("settleCheckoutId", params.settleCheckoutId);
  }
  return apiClient
    .post<BankTransferCheckoutResponse>(
      "/tenants/current/plan-checkout/bank-transfer",
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    )
    .then((res) => res.data);
}

/**
 * Bank-transfer checkout for a specific tenant the accountant manages —
 * mirrors createBankTransferCheckout but targets the tenant by id (URL +
 * membership check) instead of the active-tenant session, so it can't land
 * the payment on the wrong tenant if the context hasn't switched.
 *
 * Monthly is accepted since spec a9b1ccad. Note the response's `activated`
 * flag: a monthly transfer whose receipt does not verify is HELD — recorded
 * as paid, plan NOT granted, pending human review. Annual always activates.
 */
export function createBankTransferCheckoutForTenant(
  tenantId: string,
  params: {
    planId: string;
    billingInterval: "annual" | "monthly";
    receipt: File;
    /**
     * Settle this pending checkout rather than creating a new one — same
     * contract as the active-tenant function above, and required for the same
     * reason: the accountant surfaces now open a checkout when the panel opens
     * so the payer can be shown its reference, and minting a second row at
     * submit would make that reference match nothing.
     */
    settleCheckoutId?: string;
  }
): Promise<BankTransferCheckoutResponse> {
  const formData = new FormData();
  formData.append("receipt", params.receipt);
  formData.append("planId", params.planId);
  formData.append("billingInterval", params.billingInterval);
  if (params.settleCheckoutId) {
    formData.append("settleCheckoutId", params.settleCheckoutId);
  }
  return apiClient
    .post<BankTransferCheckoutResponse>(
      `/tenants/${tenantId}/plan-checkout/bank-transfer`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    )
    .then((res) => res.data);
}

export interface BankTransferIntentResponse {
  /** The row the payer will wire against — and the id the reference comes from. */
  checkoutId: string;
  amountMinor: number;
  currency: string;
  /** True when an existing pending checkout was adopted rather than a new one created. */
  reused: boolean;
}

/**
 * Open a checkout for a bank transfer the payer has not made yet.
 *
 * Called when the transfer panel OPENS, because the payment reference the
 * payer types into their bank is derived from the checkout id: without a row
 * there is nothing to show them, and a wire with no reference — or with
 * whatever text they invent — is one the receipt verifier reads as a mismatch.
 * Pass the returned checkoutId back as `settleCheckoutId` on submit.
 */
export function createBankTransferIntent(params: {
  planId: string;
  billingInterval: "annual" | "monthly";
  returnContext: PlanCheckoutReturnContext;
}): Promise<BankTransferIntentResponse> {
  return apiClient
    .post<BankTransferIntentResponse>(
      "/tenants/current/plan-checkout/bank-transfer/intent",
      params,
    )
    .then((res) => res.data);
}

/**
 * The same, for a tenant the accountant manages — tenant id from the URL, not
 * from the session, so the checkout (and therefore the reference the client is
 * given) cannot land on whichever tenant happens to be active.
 */
export function createBankTransferIntentForTenant(
  tenantId: string,
  params: {
    planId: string;
    billingInterval: "annual" | "monthly";
    returnContext: PlanCheckoutReturnContext;
  },
): Promise<BankTransferIntentResponse> {
  return apiClient
    .post<BankTransferIntentResponse>(
      `/tenants/${tenantId}/plan-checkout/bank-transfer/intent`,
      params,
    )
    .then((res) => res.data);
}

export interface PlanCheckoutStatusResponse {
  checkoutId: string;
  status: string;
  amountMinor: number;
  currency: string;
  planId: string;
  billingInterval: string;
  returnContext: string;
  paidAt: string | null;
  shortUrl: string | null;
}

export function getPlanCheckout(
  checkoutId: string,
  paymentIdFromRedirect?: string | null
): Promise<PlanCheckoutStatusResponse> {
  const search =
    paymentIdFromRedirect && paymentIdFromRedirect.trim() !== ""
      ? `?payment_id=${encodeURIComponent(paymentIdFromRedirect.trim())}`
      : "";
  return apiClient
    .get<PlanCheckoutStatusResponse>(`/tenants/current/plan-checkouts/${checkoutId}${search}`)
    .then((res) => res.data);
}

/**
 * Latest pending plan_checkout for the active tenant — backend
 * source-of-truth that replaces per-browser localStorage tracking
 * on the accountant client onboarding card.
 */
export function getPendingPlanCheckout(): Promise<PlanCheckoutStatusResponse | null> {
  return apiClient
    .get<PlanCheckoutStatusResponse | null>(
      "/tenants/current/pending-plan-checkout",
    )
    .then((res) => res.data);
}

/**
 * Pending plan_checkout for a specific tenant the user manages.
 * Powers the "Migrar a plan pago" dialog so the accountant can
 * recover an in-flight share link instead of regenerating it (which
 * cancels the prior URL).
 */
export function getPendingPlanCheckoutForTenant(
  tenantId: string,
): Promise<PlanCheckoutStatusResponse | null> {
  return apiClient
    .get<PlanCheckoutStatusResponse | null>(
      `/tenants/${tenantId}/pending-plan-checkout`,
    )
    .then((res) => res.data);
}

export function cancelPlanCheckout(
  checkoutId: string,
): Promise<{ ok: true; status: string }> {
  return apiClient
    .post<{ ok: true; status: string }>(
      `/tenants/current/plan-checkouts/${checkoutId}/cancel`,
    )
    .then((res) => res.data);
}

export function cancelPlanCheckoutForTenant(
  tenantId: string,
  checkoutId: string,
): Promise<{ ok: true; status: string }> {
  return apiClient
    .post<{ ok: true; status: string }>(
      `/tenants/${tenantId}/plan-checkouts/${checkoutId}/cancel`,
    )
    .then((res) => res.data);
}

/**
 * Single plan_checkout by id for a tenant the user manages.
 * Used by the accountant Migrar dialog to poll for payment
 * completion without forcing a global tenant refresh.
 */
export function getPlanCheckoutForTenant(
  tenantId: string,
  checkoutId: string,
): Promise<PlanCheckoutStatusResponse> {
  return apiClient
    .get<PlanCheckoutStatusResponse>(
      `/tenants/${tenantId}/plan-checkouts/${checkoutId}`,
    )
    .then((res) => res.data);
}

export interface CreateMercadoPagoSubscriptionParams {
  /** Single-use token from the Payment Brick. */
  cardTokenId: string;
  /** The payer's Mercado Pago account email, as the Brick collected it. */
  payerEmail: string;
  /** MP's method id. Required for the ANNUAL one-off Order (credit vs debit). */
  paymentMethodId?: string;
  /**
   * Instalments the payer chose. ANNUAL only, and ADVISORY — the server
   * clamps it to what the plan permits, since the Brick's cap is a UI
   * affordance a crafted request ignores.
   */
  installments?: number;
}

export interface CreateMercadoPagoSubscriptionResponse {
  preapprovalId: string;
  /** pending | authorized | paused | cancelled, straight from Mercado Pago. */
  status: string;
}

/**
 * Second half of a Mercado Pago checkout: hand the backend the card token the
 * Brick produced so it can create the subscription.
 *
 * The AMOUNT IS NOT SENT. The Brick displays a figure and submits one back, but
 * the server charges the amount on the checkout row it already wrote — a
 * client-supplied price on a billing call is a discount anyone can grant
 * themselves.
 */
export function createMercadoPagoSubscription(
  checkoutId: string,
  params: CreateMercadoPagoSubscriptionParams
): Promise<CreateMercadoPagoSubscriptionResponse> {
  return apiClient
    .post<CreateMercadoPagoSubscriptionResponse>(
      `/tenants/current/plan-checkout/${encodeURIComponent(checkoutId)}/mercadopago`,
      params
    )
    .then((res) => res.data);
}

/**
 * The public checkout surface, for a payer with no session — an accountant's
 * client following a shared link.
 *
 * Authorised by the short token from the URL, which is what the share link
 * hands out. Returns only what the Brick needs to render: no company, no
 * tenant name, no plan id.
 */
export interface PublicCheckoutResponse {
  amountMinor: number;
  currency: string;
  billingInterval: string;
  /** What the payer writes in their bank's reference field, if they transfer. */
  paymentReference: string;
}

export function getPublicCheckout(
  token: string
): Promise<PublicCheckoutResponse> {
  return apiClient
    .get<PublicCheckoutResponse>(`/p/${encodeURIComponent(token)}/checkout`)
    .then((res) => res.data);
}

/** Hand over the Brick's card token without a session. */
export function createPublicMercadoPagoSubscription(
  token: string,
  params: CreateMercadoPagoSubscriptionParams
): Promise<CreateMercadoPagoSubscriptionResponse> {
  return apiClient
    .post<CreateMercadoPagoSubscriptionResponse>(
      `/p/${encodeURIComponent(token)}/mercadopago`,
      params
    )
    .then((res) => res.data);
}
